import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Combobox,
  ConfirmDialog,
  Drawer,
  Input,
  KpiTile,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Switch,
  Textarea,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi, UNIT_DIMENSIONS } from '../catalog/catalogApi';
import { useCatalogItemPicker } from '../catalog/useCatalogItemPicker';
import { settingsApi } from '../settings/settingsApi';
import { recipesApi, recipeCategoriesApi, RECIPE_TYPES, recipeStatusMeta } from './recipesApi';
import './recipes.css';

let tmpId = 0;
const tmpKey = () => `rc-${++tmpId}`;

const newComponent = (over = {}) => ({
  key: tmpKey(),
  type: 'Item',
  itemId: '',
  subRecipeId: '',
  name: '',
  quantity: '',
  uomId: '',
  wastagePercent: '',
  notes: '',
  ...over,
});

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

const TYPE_OPTIONS = [
  { value: 'Item', label: 'Item' },
  { value: 'SubRecipe', label: 'Sub-recipe' },
];

// Display labels for status-change toasts (server enum → English, translated at render).
const STATUS_CHANGED_TOAST = {
  Active: 'Recipe activated',
  Archived: 'Recipe archived',
  Draft: 'Recipe set to draft',
};

export default function RecipeFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { activePropertyId, hasPermission } = useProperty();
  const canManage = hasPermission('recipes.manage');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const itemPicker = useCatalogItemPicker({ propertyId: activePropertyId });
  const items = itemPicker.items;
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recipeOptionsRaw, setRecipeOptionsRaw] = useState([]);
  const [currencies, setCurrencies] = useState(['USD']);

  const [recipe, setRecipe] = useState(null); // last server-costed recipe (for cost panel)
  const [status, setStatus] = useState('Draft');
  const [name, setName] = useState('');
  const [type, setType] = useState('Preparation');
  const [categoryId, setCategoryId] = useState('');
  const [yieldQuantity, setYieldQuantity] = useState('1');
  const [yieldUomId, setYieldUomId] = useState('');
  const [portions, setPortions] = useState('1');
  const [sellingPrice, setSellingPrice] = useState('');
  const [targetFoodCost, setTargetFoodCost] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('');
  const [components, setComponents] = useState([newComponent()]);

  // Inline "add new" for Category / Yield unit, so users don't have to leave the form.
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  const [catName, setCatName] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const UNIT_FORM_EMPTY = { name: '', code: '', dimension: 'Count', isBaseUnit: false, factorToBase: 1 };
  const [unitDrawerOpen, setUnitDrawerOpen] = useState(false);
  const [unitForm, setUnitForm] = useState(UNIT_FORM_EMPTY);
  const [unitSaving, setUnitSaving] = useState(false);

  const isMenuItem = type === 'MenuItem';

  const loadLookups = useCallback(async () => {
    try {
      const [us, cats, recs, eff] = await Promise.all([
        catalogApi.listUnits().catch(() => null),
        recipeCategoriesApi.list().catch(() => null),
        recipesApi.list().catch(() => null),
        settingsApi.getEffective(activePropertyId).catch(() => null),
      ]);
      setUnits(us || []);
      setCategories(cats || []);
      setRecipeOptionsRaw(recs || []);
      const ccys = eff?.currencies?.length ? eff.currencies : ['USD'];
      setCurrencies(ccys);
      if (!editing) setCurrency(eff?.defaultCurrency || ccys[0] || 'USD');
    } catch {
      // Non-fatal.
    }
  }, [activePropertyId, editing]);

  const loadRecipe = useCallback(async () => {
    if (!editing) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await recipesApi.get(id);
      setRecipe(r);
      setStatus(r.status);
      setName(r.name || '');
      setType(r.type || 'Preparation');
      setCategoryId(r.categoryId || '');
      setYieldQuantity(String(r.yieldQuantity ?? 1));
      setYieldUomId(r.yieldUomId || '');
      setPortions(String(r.portions ?? 1));
      setSellingPrice(r.sellingPrice != null ? String(r.sellingPrice) : '');
      setTargetFoodCost(r.targetFoodCostPercent != null ? String(r.targetFoodCostPercent) : '');
      setCurrency(r.currency || 'USD');
      setNotes(r.notes || '');
      setComponents([
        ...(r.components || []).map((c) => newComponent({
          type: c.type,
          itemId: c.itemId || '',
          subRecipeId: c.subRecipeId || '',
          name: c.name || '',
          quantity: String(c.quantity ?? ''),
          uomId: c.uomId || '',
          wastagePercent: c.wastagePercent ? String(c.wastagePercent) : '',
          notes: c.notes || '',
          lineCost: c.lineCost,
          costKnown: c.costKnown,
          issue: c.issue,
        })),
      ]);
      setDirty(false);
    } catch (err) {
      toast.error(t('Could not load recipe'), { description: err?.message });
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  }, [editing, id, navigate, toast, t]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { loadRecipe(); }, [loadRecipe]);

  // Pull in items an existing recipe references but the first catalog page didn't include.
  useEffect(() => {
    itemPicker.ensureIds([...new Set(components.map((c) => c.itemId).filter(Boolean))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components]);

  const itemOptions = itemPicker.options;
  const subRecipeOptions = useMemo(
    () => recipeOptionsRaw
      .filter((r) => r.id !== id)
      .map((r) => ({ value: r.id, label: r.name, description: r.number })),
    [recipeOptionsRaw, id],
  );
  const uomOptions = useMemo(
    () => [{ value: '', label: '—' }, ...units.map((u) => ({ value: u.id, label: u.code || u.name }))],
    [units],
  );
  const categoryOptions = useMemo(
    () => [{ value: '', label: t('— None —') }, ...categories.map((c) => ({ value: c.id, label: c.name }))],
    [categories, t],
  );
  const currencyOptions = useMemo(() => currencies.map((c) => ({ value: c, label: c })), [currencies]);
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const markDirty = () => setDirty(true);

  const createCategory = async (e) => {
    e.preventDefault();
    const nm = catName.trim();
    if (!nm) return;
    setCatSaving(true);
    try {
      const created = await recipeCategoriesApi.create({ name: nm });
      const cats = (await recipeCategoriesApi.list()) || [];
      setCategories(cats);
      const newId = created?.id || cats.find((c) => c.name === nm)?.id || '';
      if (newId) { setCategoryId(newId); markDirty(); }
      setCatName('');
      setCatDrawerOpen(false);
      toast.success(t('Category created'));
    } catch (err) {
      toast.error(t('Could not create category'), { description: err?.message });
    } finally {
      setCatSaving(false);
    }
  };

  const createUnit = async (e) => {
    e.preventDefault();
    if (!unitForm.name.trim() || !unitForm.code.trim()) return;
    setUnitSaving(true);
    try {
      const payload = {
        name: unitForm.name.trim(),
        code: unitForm.code.trim(),
        dimension: unitForm.dimension,
        isBaseUnit: unitForm.isBaseUnit,
        factorToBase: unitForm.isBaseUnit ? 1 : Number(unitForm.factorToBase) || 1,
        isActive: true,
      };
      const created = await catalogApi.createUnit(payload);
      const us = (await catalogApi.listUnits()) || [];
      setUnits(us);
      const newId = created?.id || us.find((u) => u.code === payload.code)?.id || '';
      if (newId) { setYieldUomId(newId); markDirty(); }
      setUnitForm(UNIT_FORM_EMPTY);
      setUnitDrawerOpen(false);
      toast.success(t('Unit created'));
    } catch (err) {
      toast.error(t('Could not create unit'), { description: err?.message });
    } finally {
      setUnitSaving(false);
    }
  };

  const updateComponent = (key, patch) => {
    markDirty();
    setComponents((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  };
  const removeComponent = (key) => {
    markDirty();
    setComponents((prev) => prev.filter((c) => c.key !== key));
  };
  const addComponent = () => setComponents((prev) => [...prev, newComponent()]);

  const selectItem = (key, itemId) => {
    const item = itemsById.get(itemId);
    updateComponent(key, {
      itemId,
      name: item?.name || '',
      uomId: components.find((c) => c.key === key)?.uomId || item?.stockUomId || '',
    });
  };

  const validComponents = useMemo(
    () => components.filter((c) =>
      (c.type === 'Item' ? c.itemId : c.subRecipeId) && num(c.quantity) > 0),
    [components],
  );

  const fmtMoney = (n) => `${currency || 'USD'} ${formatAmount(n)}`;

  const buildPayload = () => ({
    name: name.trim(),
    type,
    categoryId: categoryId || null,
    yieldQuantity: num(yieldQuantity) ?? 1,
    yieldUomId: yieldUomId || null,
    portions: num(portions) ?? 1,
    sellingPrice: isMenuItem ? (num(sellingPrice) ?? null) : null,
    targetFoodCostPercent: isMenuItem ? (num(targetFoodCost) ?? null) : null,
    currency: (currency || 'USD').toUpperCase(),
    notes: notes.trim() || null,
    components: validComponents.map((c) => ({
      type: c.type,
      itemId: c.type === 'Item' ? c.itemId : null,
      subRecipeId: c.type === 'SubRecipe' ? c.subRecipeId : null,
      quantity: num(c.quantity) ?? 0,
      uomId: c.uomId || null,
      wastagePercent: num(c.wastagePercent) ?? 0,
      notes: c.notes?.trim() || null,
    })),
  });

  const validate = () => {
    if (!name.trim()) { toast.error(t('Enter a recipe name.')); return false; }
    if (validComponents.length === 0) { toast.error(t('Add at least one ingredient with a quantity.')); return false; }
    return true;
  };

  const save = async () => {
    if (!canManage || !validate()) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        await recipesApi.update(id, payload);
        // Reload to refresh server-computed line costs and totals.
        await loadRecipe();
        toast.success(t('Recipe saved'));
      } else {
        const created = await recipesApi.create(payload);
        toast.success(t('{number} created', { number: created.number }));
        navigate(`/recipes/${created.id}`);
      }
    } catch (err) {
      toast.error(t('Could not save recipe'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (next) => {
    setSubmitting(true);
    try {
      const updated = await recipesApi.changeStatus(id, next);
      setRecipe(updated);
      setStatus(updated.status);
      toast.success(t(STATUS_CHANGED_TOAST[next] || 'Recipe saved'));
    } catch (err) {
      toast.error(t('Could not change status'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    setSubmitting(true);
    try {
      await recipesApi.remove(id);
      toast.success(t('Recipe deleted'));
      navigate('/recipes');
    } catch (err) {
      toast.error(t('Could not delete recipe'), { description: err?.message });
      setDeleteOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }

  const statusMeta = recipeStatusMeta(status);

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/recipes')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Recipes')}
      </Button>

      <PageHeader
        title={editing ? (name || t('Recipe')) : t('New recipe')}
        description={t('Build a costed ingredient list. Costs roll up from live item prices and nested sub-recipes.')}
        icon={<Icon.ConciergeBell size={22} />}
        actions={
          editing && (
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge>
              {recipe?.number && <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-foreground-subtle)' }}>{recipe.number}</span>}
            </div>
          )
        }
      />

      <div className="rc-layout">
        <div className="rc-main">
          <Card>
            <Card.Body>
              <div className="rc-form">
                <Input label={t('Name')} placeholder={t('e.g. Tomato sauce / Margherita pizza')} value={name} onChange={(e) => { setName(e.target.value); markDirty(); }} disabled={!canManage} autoFocus />
                <div className="rc-grid">
                  <div className="ps-field">
                    <span className="ps-field__label">{t('Type')}</span>
                    <Select value={type} onChange={(v) => { setType(v); markDirty(); }} options={RECIPE_TYPES.map((o) => ({ ...o, label: t(o.label) }))} ariaLabel={t('Type')} disabled={!canManage} />
                  </div>
                  <div className="ps-field">
                    <div className="rc-field-head">
                      <span className="ps-field__label">{t('Category')}</span>
                      {canManage && (
                        <button type="button" className="rc-inline-add" onClick={() => setCatDrawerOpen(true)}>
                          <Icon.Plus size={12} /> {t('New')}
                        </button>
                      )}
                    </div>
                    <Select value={categoryId} onChange={(v) => { setCategoryId(v); markDirty(); }} options={categoryOptions} ariaLabel={t('Category')} disabled={!canManage} searchable />
                  </div>
                </div>
                <div className="rc-grid">
                  <Input label={t('Yield qty')} type="number" value={yieldQuantity} onChange={(e) => { setYieldQuantity(e.target.value); markDirty(); }} disabled={!canManage} />
                  <div className="ps-field">
                    <div className="rc-field-head">
                      <span className="ps-field__label">{t('Yield unit')}</span>
                      {canManage && (
                        <button type="button" className="rc-inline-add" onClick={() => setUnitDrawerOpen(true)}>
                          <Icon.Plus size={12} /> {t('New')}
                        </button>
                      )}
                    </div>
                    <Select value={yieldUomId} onChange={(v) => { setYieldUomId(v); markDirty(); }} options={uomOptions} ariaLabel={t('Yield unit')} disabled={!canManage} searchable />
                  </div>
                  <Input label={t('Portions')} type="number" value={portions} onChange={(e) => { setPortions(e.target.value); markDirty(); }} hint={t('Servings the yield produces')} disabled={!canManage} />
                </div>
                {isMenuItem && (
                  <div className="rc-grid">
                    <Input label={t('Selling price (per portion)')} type="number" value={sellingPrice} onChange={(e) => { setSellingPrice(e.target.value); markDirty(); }} disabled={!canManage} />
                    <div className="ps-field">
                      <span className="ps-field__label">{t('Currency')}</span>
                      <Select value={currency} onChange={(v) => { setCurrency(v); markDirty(); }} options={currencyOptions} ariaLabel={t('Currency')} disabled={!canManage} />
                    </div>
                    <Input label={t('Target food cost %')} type="number" value={targetFoodCost} onChange={(e) => { setTargetFoodCost(e.target.value); markDirty(); }} hint={t('Flag when over target')} disabled={!canManage} />
                  </div>
                )}
                <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => { setNotes(e.target.value); markDirty(); }} placeholder={t('Method, plating notes (optional)')} disabled={!canManage} />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>{t('Ingredients')}</Card.Title>
              {canManage && <Button variant="ghost" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={addComponent}>{t('Add ingredient')}</Button>}
            </Card.Header>
            <Card.Body>
              {components.length === 0 ? (
                <div className="rc-empty">{t('No ingredients yet.')}</div>
              ) : (
                <div className="rc-lines">
                  <div className="rc-line rc-line--head">
                    <span>{t('Kind')}</span>
                    <span>{t('Ingredient')}</span>
                    <span>{t('Qty')}</span>
                    <span>{t('Unit')}</span>
                    <span>{t('Waste %')}</span>
                    <span className="rc-line__cost">{t('Cost')}</span>
                    <span />
                  </div>
                  {components.map((c) => {
                    const showCost = !dirty && c.lineCost != null;
                    return (
                      <div className="rc-line" key={c.key}>
                        <Select
                          size="sm"
                          value={c.type}
                          onChange={(v) => updateComponent(c.key, { type: v, itemId: '', subRecipeId: '', name: '' })}
                          options={TYPE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                          ariaLabel={t('Kind')}
                          disabled={!canManage}
                        />
                        <div className="rc-line__picker">
                          {c.type === 'Item' ? (
                            <Combobox
                              options={itemOptions}
                              value={c.itemId}
                              onChange={(v) => selectItem(c.key, v)}
                              onSearch={itemPicker.onSearchChange}
                              placeholder={t('Search items…')}
                              searchPlaceholder={t('Search name or SKU…')}
                              size="sm"
                              emptyText={itemPicker.searching ? t('Searching…') : t('No item matches')}
                              ariaLabel={t('Item')}
                            />
                          ) : (
                            <Combobox
                              options={subRecipeOptions}
                              value={c.subRecipeId}
                              onChange={(v) => updateComponent(c.key, { subRecipeId: v, name: subRecipeOptions.find((o) => o.value === v)?.label || '' })}
                              placeholder={t('Search recipes…')}
                              searchPlaceholder={t('Search recipes…')}
                              size="sm"
                              emptyText={t('No recipe matches')}
                              ariaLabel={t('Sub-recipe')}
                            />
                          )}
                        </div>
                        <Input size="sm" type="number" value={c.quantity} placeholder="0" onChange={(e) => updateComponent(c.key, { quantity: e.target.value })} disabled={!canManage} />
                        <Select size="sm" value={c.uomId} onChange={(v) => updateComponent(c.key, { uomId: v })} options={uomOptions} ariaLabel={t('Unit')} disabled={!canManage} searchable />
                        <Input size="sm" type="number" value={c.wastagePercent} placeholder="0" onChange={(e) => updateComponent(c.key, { wastagePercent: e.target.value })} disabled={!canManage} />
                        <span className="rc-line__cost">
                          {showCost ? (
                            c.costKnown ? fmtMoney(c.lineCost) : <Icon.CircleAlert size={14} title={c.issue || t('No cost')} style={{ color: 'var(--color-warning)' }} />
                          ) : <span className="rc-muted">—</span>}
                        </span>
                        {canManage
                          ? <IconButton aria-label={t('Remove')} variant="ghost" size="icon-sm" onClick={() => removeComponent(c.key)}><Icon.Trash size={15} /></IconButton>
                          : <span />}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="rc-side">
          <Card>
            <Card.Header><Card.Title>{t('Cost summary')}</Card.Title></Card.Header>
            <Card.Body>
              {recipe ? (
                <div className="rc-cost">
                  <KpiTile label={t('Total batch cost')} value={fmtMoney(recipe.totalCost)} />
                  <KpiTile label={t('Cost / portion')} value={fmtMoney(recipe.costPerPortion)} />
                  {isMenuItem && (
                    <>
                      <KpiTile
                        label={t('Food cost %')}
                        value={recipe.foodCostPercent == null ? '—' : (
                          <span style={{ color: recipe.foodCostPercent > 35 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                            {recipe.foodCostPercent.toFixed(1)}%
                          </span>
                        )}
                      />
                      <KpiTile
                        label={t('Gross margin / portion')}
                        value={recipe.grossMargin == null ? '—' : fmtMoney(recipe.grossMargin)}
                        meta={recipe.grossMarginPercent != null ? t('{percent}% margin', { percent: recipe.grossMarginPercent.toFixed(1) }) : undefined}
                      />
                    </>
                  )}
                  {recipe.hasUncostable && (
                    <div className="rc-warn">
                      <Icon.CircleAlert size={14} /> {t('Some ingredients have no cost or an incompatible unit — total is understated.')}
                    </div>
                  )}
                  {dirty && <div className="rc-muted rc-hint">{t('Unsaved changes — save to recalculate.')}</div>}
                </div>
              ) : (
                <div className="rc-muted">{t('Save the recipe to compute its cost.')}</div>
              )}
            </Card.Body>
          </Card>

          {canManage && (
            <Card>
              <Card.Body>
                <div className="rc-actions">
                  <Button variant="primary" loading={submitting} leadingIcon={<Icon.Check size={15} />} onClick={save} fullWidth>
                    {editing ? t('Save recipe') : t('Create recipe')}
                  </Button>
                  {editing && status !== 'Active' && (
                    <Button variant="secondary" disabled={submitting} onClick={() => changeStatus('Active')} fullWidth>{t('Mark active')}</Button>
                  )}
                  {editing && status === 'Active' && (
                    <Button variant="secondary" disabled={submitting} onClick={() => changeStatus('Archived')} fullWidth>{t('Archive')}</Button>
                  )}
                  {editing && (
                    <Button variant="destructive" disabled={submitting} leadingIcon={<Icon.Trash size={15} />} onClick={() => setDeleteOpen(true)} fullWidth>{t('Delete')}</Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      <Drawer
        open={catDrawerOpen}
        onClose={() => (catSaving ? null : setCatDrawerOpen(false))}
        title={t('New menu category')}
        description={t('Sections like Appetizers, Mains or Desserts. Manage them all under Categories.')}
        size="sm"
      >
        <form className="pp-form" onSubmit={createCategory}>
          <Input label={t('Name')} placeholder={t('e.g. Appetizers')} value={catName} onChange={(e) => setCatName(e.target.value)} autoFocus />
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setCatDrawerOpen(false)} disabled={catSaving}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={catSaving}>{t('Create category')}</Button>
          </div>
        </form>
      </Drawer>

      <Drawer
        open={unitDrawerOpen}
        onClose={() => (unitSaving ? null : setUnitDrawerOpen(false))}
        title={t('New unit of measure')}
        description={t('Units are shared across the whole system (stock, purchasing, recipes).')}
        size="md"
      >
        <form className="pp-form" onSubmit={createUnit}>
          <div className="pp-form__row">
            <Input label={t('Name')} placeholder={t('Kilogram')} value={unitForm.name} onChange={(e) => setUnitForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
            <Input label={t('Code')} placeholder="kg" value={unitForm.code} onChange={(e) => setUnitForm((f) => ({ ...f, code: e.target.value }))} />
          </div>
          <div className="ps-field">
            <span className="ps-field__label">{t('Dimension')}</span>
            <Select value={unitForm.dimension} onChange={(v) => setUnitForm((f) => ({ ...f, dimension: v }))} options={UNIT_DIMENSIONS.map((d) => ({ ...d, label: t(d.label) }))} ariaLabel={t('Dimension')} />
          </div>
          <Switch checked={unitForm.isBaseUnit} onChange={(v) => setUnitForm((f) => ({ ...f, isBaseUnit: v }))} label={t('Base unit for its dimension')} hint={t('The canonical unit others convert to (factor = 1).')} />
          {!unitForm.isBaseUnit && (
            <Input label={t('Factor to base')} type="number" placeholder="1000" hint={t('How many base units equal one of this unit.')} value={unitForm.factorToBase} onChange={(e) => setUnitForm((f) => ({ ...f, factorToBase: e.target.value }))} />
          )}
          <div className="pp-form__actions">
            <Button type="button" variant="ghost" onClick={() => setUnitDrawerOpen(false)} disabled={unitSaving}>{t('Cancel')}</Button>
            <Button type="submit" variant="primary" loading={unitSaving}>{t('Create unit')}</Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDialog
        open={deleteOpen}
        title={t('Delete recipe')}
        description={t('This permanently removes the recipe. Recipes used as a sub-recipe elsewhere cannot be deleted.')}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={submitting}
        onConfirm={doDelete}
        onCancel={() => (submitting ? null : setDeleteOpen(false))}
      />
    </PageContainer>
  );
}
